import { useEffect, useRef, useState } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

type BaseProps = {
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  needsClarification?: boolean;
  ariaLabel?: string;
};

type SelectProps = BaseProps & {
  kind: 'select';
  value: string | null;
  options: Array<{ value: string; label: string }>;
  onCommit: (value: string | null) => void;
};

type NumberProps = BaseProps & {
  kind: 'number';
  value: number | null;
  min?: number;
  step?: number;
  onCommit: (value: number | null) => void;
};

type EditableSpecCellProps = SelectProps | NumberProps;

const cellBase =
  'w-full appearance-none rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-sm text-foreground cursor-pointer ' +
  'hover:border-input hover:bg-accent/40 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const missingCls = 'text-muted-foreground underline decoration-dotted decoration-warning underline-offset-2';

// Radix Select forbids an empty-string item value, so the "clear back to —" row
// uses this sentinel and is mapped back to null on commit (86d3c38ve).
const CLEAR_VALUE = '__clear__';

export function EditableSpecCell(props: EditableSpecCellProps) {
  if (props.kind === 'select') {
    return <SelectCell {...props} />;
  }
  return <NumberCell {...props} />;
}

// Radix Select instead of a native <select> so the option popup is themed via
// --popover tokens in dark / Industrial (86d3c38ve). The trigger is a custom
// borderless button that mimics the old inline cell: plain text at rest, with a
// caret + hover/focus affordance only on interaction so the dense table reads
// the same. Keyboard model is Radix's (Space/Enter/↑↓ to open & pick, Esc to
// cancel, Tab to leave); the option list mounts in a portal only while open.
function SelectCell(props: SelectProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (props.autoFocus) triggerRef.current?.focus();
  }, [props.autoFocus]);

  return (
    <Select
      value={props.value ?? ''}
      disabled={props.disabled}
      onValueChange={(v) => props.onCommit(v === CLEAR_VALUE ? null : v)}
    >
      <SelectPrimitive.Trigger
        ref={triggerRef}
        aria-label={props.ariaLabel}
        data-editable-cell=""
        className={cn(
          'group flex w-full cursor-pointer items-center justify-between gap-1 rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-left text-sm text-foreground',
          'hover:border-input hover:bg-accent/40 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring',
          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60',
          props.value == null && missingCls,
          props.className,
        )}
      >
        <SelectValue placeholder={props.placeholder ?? '—'} />
        <ChevronDown
          aria-hidden
          className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-50 group-focus:opacity-50 group-data-[state=open]:opacity-50"
        />
      </SelectPrimitive.Trigger>
      <SelectContent>
        <SelectItem value={CLEAR_VALUE}>{props.placeholder ?? '—'}</SelectItem>
        {props.options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NumberCell(props: NumberProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string>(props.value != null ? String(props.value) : '');
  // Bumped on every commit so the field re-reads the stored value even when it did not change: the store may
  // normalize what was typed (QUO-R-005 rounds 100.2 up to 101), and if the line already held 101 the value
  // prop never changes — without this the input would keep showing the "100.2" that was never stored.
  const [commitSeq, setCommitSeq] = useState(0);

  useEffect(() => {
    setDraft(props.value != null ? String(props.value) : '');
  }, [props.value, commitSeq]);

  useEffect(() => {
    if (props.autoFocus) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [props.autoFocus]);

  const commit = (raw: string) => {
    if (raw === '') {
      if (props.value != null) props.onCommit(null);
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n)) {
      setDraft(props.value != null ? String(props.value) : '');
      return;
    }
    if (n !== props.value) {
      props.onCommit(n);
      setCommitSeq((s) => s + 1);
    }
  };

  return (
    <input
      ref={ref}
      type="number"
      inputMode="decimal"
      className={cn(
        cellBase,
        'text-right tabular-nums',
        // Hide the native number spinner. `appearance-none` (in cellBase) alone does
        // NOT remove the WebKit spin buttons — they stay pinned to the input's right
        // edge and overlay the right-aligned digits, hiding the last character(s)
        // (ClickUp 86d3f8234 / 86d3fb5yh). Reset the pseudo-elements + Firefox's textfield mode.
        '[appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none',
        props.value == null && missingCls,
        props.className,
      )}
      value={draft}
      min={props.min}
      step={props.step}
      disabled={props.disabled}
      placeholder={props.placeholder ?? '—'}
      aria-label={props.ariaLabel}
      data-editable-cell=""
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => commit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(props.value != null ? String(props.value) : '');
          e.currentTarget.blur();
        }
      }}
    />
  );
}
